import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import moment from 'moment-jalaali';
import { toast } from 'react-toastify';
import supplierService, {
  computeInvoiceTotals,
  PurchaseInvoice,
  PurchaseInvoiceInput,
  Supplier,
} from '../../services/supplierService';
import productManagementService from '../../services/productManagement.service';
import { formatPersianMoneyInput, parsePersianMoneyInput } from '../../utils/persianNumber';
import JalaliDateField from '../common/JalaliDateField';
import { faNum, faPercent, faToman, marginOf } from './format';

interface Props {
  open: boolean;
  suppliers: Supplier[];
  defaultSupplierId?: string;
  onClose: () => void;
  onSaved: (inv: PurchaseInvoice) => void;
}

interface ProductOption {
  _id: string;
  name: string;
  price?: number;
  sku?: string;
}

interface LineDraft {
  key: number;
  product: ProductOption | null;
  productCode: string;
  name: string;
  brand: string;
  quantity: string;
  unitBasePriceRial: string;
  unitCostToman: string;
  unitSaleToman: string;
  note: string;
}

let lineSeq = 1;
const newLine = (): LineDraft => ({
  key: lineSeq++,
  product: null,
  productCode: '',
  name: '',
  brand: '',
  quantity: '1',
  unitBasePriceRial: '',
  unitCostToman: '',
  unitSaleToman: '',
  note: '',
});

const toNum = (s: string): number => Number(parsePersianMoneyInput(s || '')) || 0;

/** Product search box backed by the admin product list endpoint. */
const ProductPicker: React.FC<{ value: ProductOption | null; onChange: (p: ProductOption | null) => void }> = ({ value, onChange }) => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<ProductOption[]>([]);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  useEffect(() => {
    const q = input.trim();
    if (q.length < 2) {
      setOptions(value ? [value] : []);
      return;
    }
    const id = ++reqId.current;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await productManagementService.getProducts({ search: q, limit: 15 });
        if (id === reqId.current) {
          setOptions(res.items.map((p) => ({ _id: p._id, name: p.name, price: p.price, sku: p.sku })));
        }
      } catch {
        if (id === reqId.current) setOptions([]);
      } finally {
        if (id === reqId.current) setLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [input, value]);

  return (
    <Autocomplete<ProductOption>
      size="small"
      value={value}
      options={options}
      loading={loading}
      filterOptions={(x) => x}
      isOptionEqualToValue={(a, b) => a._id === b._id}
      getOptionLabel={(o) => o.name || ''}
      onChange={(_, v) => onChange(v)}
      onInputChange={(_, v) => setInput(v)}
      noOptionsText={input.trim().length < 2 ? 'حداقل ۲ حرف بنویسید' : 'محصولی یافت نشد'}
      renderInput={(params) => (
        <TextField
          {...params}
          label="اتصال به محصول (اختیاری)"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

const MoneyField: React.FC<{ label: string; value: string; onChange: (v: string) => void; required?: boolean }> = ({
  label,
  value,
  onChange,
  required,
}) => (
  <TextField
    size="small"
    label={label}
    value={formatPersianMoneyInput(value)}
    onChange={(e) => onChange(parsePersianMoneyInput(e.target.value))}
    required={required}
    fullWidth
    inputProps={{ dir: 'ltr', inputMode: 'numeric' }}
  />
);

const InvoiceFormDialog: React.FC<Props> = ({ open, suppliers, defaultSupplierId, onClose, onSaved }) => {
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [supplier, setSupplier] = useState('');
  const [dateJalali, setDateJalali] = useState('');
  const [paidRial, setPaidRial] = useState('');
  const [discount, setDiscount] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([newLine()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setInvoiceNumber('');
    setSupplier(defaultSupplierId || '');
    setDateJalali(moment().format('jYYYY/jMM/jDD'));
    setPaidRial('');
    setDiscount('');
    setNotes('');
    setLines([newLine()]);
  }, [open, defaultSupplierId]);

  const patchLine = (key: number, patch: Partial<LineDraft>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const parsedLines = useMemo(
    () =>
      lines.map((l) => ({
        quantity: toNum(l.quantity),
        unitBasePriceRial: toNum(l.unitBasePriceRial),
        unitCostToman: toNum(l.unitCostToman),
        unitSaleToman: toNum(l.unitSaleToman),
        name: l.name,
      })),
    [lines],
  );
  const totals = useMemo(() => computeInvoiceTotals(parsedLines), [parsedLines]);
  const profit = totals.saleToman - totals.costToman;

  const handleSave = async () => {
    if (!invoiceNumber.trim()) return toast.error('شماره فاکتور الزامی است');
    if (!supplier) return toast.error('تأمین‌کننده را انتخاب کنید');
    const valid = lines.filter((l) => (l.name.trim() || l.product) && toNum(l.quantity) > 0);
    if (valid.length === 0) return toast.error('حداقل یک ردیف کالا با تعداد معتبر لازم است');

    if (!dateJalali) return toast.error('تاریخ فاکتور الزامی است');
    if (valid.some((l) => !Number.isInteger(toNum(l.quantity)))) return toast.error('تعداد باید عدد صحیح باشد');
    const body: PurchaseInvoiceInput = {
      invoiceNumber: invoiceNumber.trim(),
      supplier,
      invoiceDateJalali: dateJalali,
      paidAmountRial: paidRial ? toNum(paidRial) : undefined,
      discountDescription: discount.trim() || undefined,
      notes: notes.trim() || undefined,
      lines: valid.map((l) => ({
        product: l.product?._id,
        productCode: l.productCode.trim() || undefined,
        name: l.name.trim() || l.product?.name || '',
        brand: l.brand.trim() || undefined,
        quantity: toNum(l.quantity),
        unitBasePriceRial: toNum(l.unitBasePriceRial),
        unitCostToman: toNum(l.unitCostToman),
        unitSaleToman: toNum(l.unitSaleToman),
        note: l.note.trim() || undefined,
      })),
    };

    setSaving(true);
    try {
      const inv = await supplierService.createInvoice(body);
      toast.success('فاکتور خرید ثبت شد');
      onSaved(inv);
    } catch (e: any) {
      toast.error(e?.message || 'ثبت فاکتور ناموفق بود');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="lg" dir="rtl">
      <DialogTitle>ثبت فاکتور خرید</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={3}>
            <TextField
              size="small"
              label="شماره فاکتور"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              fullWidth
              required
              inputProps={{ dir: 'ltr' }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              size="small"
              label="تأمین‌کننده"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
              fullWidth
              required
            >
              {suppliers.map((s) => (
                <MenuItem key={s._id} value={s._id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <JalaliDateField label="تاریخ فاکتور" value={dateJalali} onChange={setDateJalali} width="100%" height={40} />
          </Grid>
          <Grid item xs={12} md={3}>
            <MoneyField label="مبلغ پرداختی (ریال)" value={paidRial} onChange={setPaidRial} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField size="small" label="شرح تخفیف" value={discount} onChange={(e) => setDiscount(e.target.value)} fullWidth />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField size="small" label="یادداشت" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth />
          </Grid>
        </Grid>

        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          اقلام فاکتور
        </Typography>
        {lines.map((l, idx) => (
          <Paper key={l.key} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} md={4}>
                <ProductPicker
                  value={l.product}
                  onChange={(p) =>
                    patchLine(l.key, {
                      product: p,
                      name: l.name || p?.name || '',
                      productCode: l.productCode || p?.sku || '',
                      unitSaleToman: l.unitSaleToman || (p?.price ? String(p.price) : ''),
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  size="small"
                  label="نام کالا در فاکتور"
                  value={l.name}
                  onChange={(e) => patchLine(l.key, { name: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  size="small"
                  label="کد کالا"
                  value={l.productCode}
                  onChange={(e) => patchLine(l.key, { productCode: e.target.value })}
                  fullWidth
                  inputProps={{ dir: 'ltr' }}
                />
              </Grid>
              <Grid item xs={6} md={2} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TextField
                  size="small"
                  label="برند"
                  value={l.brand}
                  onChange={(e) => patchLine(l.key, { brand: e.target.value })}
                  fullWidth
                />
                <Tooltip title="حذف ردیف">
                  <span>
                    <IconButton
                      size="small"
                      disabled={lines.length === 1}
                      onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>
              <Grid item xs={6} md={1.5}>
                <MoneyField label="تعداد" value={l.quantity} onChange={(v) => patchLine(l.key, { quantity: v })} required />
              </Grid>
              <Grid item xs={6} md={2.5}>
                <MoneyField
                  label="قیمت پایه واحد (ریال)"
                  value={l.unitBasePriceRial}
                  onChange={(v) => patchLine(l.key, { unitBasePriceRial: v })}
                />
              </Grid>
              <Grid item xs={6} md={2.5}>
                <MoneyField
                  label="بهای واحد (تومان)"
                  value={l.unitCostToman}
                  onChange={(v) => patchLine(l.key, { unitCostToman: v })}
                  required
                />
              </Grid>
              <Grid item xs={6} md={2.5}>
                <MoneyField
                  label="فروش واحد (تومان)"
                  value={l.unitSaleToman}
                  onChange={(v) => patchLine(l.key, { unitSaleToman: v })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="caption" color="text.secondary">
                  ردیف {faNum(idx + 1)} — سود: {faToman((parsedLines[idx].unitSaleToman - parsedLines[idx].unitCostToman) * parsedLines[idx].quantity)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  label="یادداشت داخلی ردیف"
                  value={l.note}
                  onChange={(e) => patchLine(l.key, { note: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Paper>
        ))}
        <Button startIcon={<AddIcon />} onClick={() => setLines((ls) => [...ls, newLine()])}>
          افزودن ردیف
        </Button>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2">تعداد اقلام: <b>{faNum(totals.quantity)}</b></Typography>
          <Typography variant="body2">جمع بهای تمام‌شده: <b>{faToman(totals.costToman)}</b></Typography>
          <Typography variant="body2">جمع فروش بالقوه: <b>{faToman(totals.saleToman)}</b></Typography>
          <Typography variant="body2">
            سود بالقوه: <b>{faToman(profit)}</b> ({faPercent(marginOf(totals.costToman, totals.saleToman))})
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          انصراف
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'در حال ثبت…' : 'ثبت فاکتور'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceFormDialog;
